import React from 'react';

import LeftArrowIcon from 'icons/left-arrow-lg.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import BookingDetails from 'components/mobileTrainingProfile/components/courseDetailsScreen/BookingDetails';
import CombinedStatus from 'components/mobileTrainingProfile/components/courseDetailsScreen/CombinedStatus';
import Evidence from 'components/mobileTrainingProfile/components/courseDetailsScreen/Evidence';
import {
  filterIncluded,
  formatResources,
} from 'components/mobileTrainingProfile/helpers/requests';
import {
  usePersonnel,
  usePersonnelTraining,
} from 'components/mobileTrainingProfile/hooks/reactQuery';

import { getTrainingStatus } from 'components/helpers/resources/training';

export default function CourseDetailsScreen({
  courseId,
  isRequired,
  onViewHomeClick,
  personnelId,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'mobileTrainingProfile.components.CourseDetailsScreen',
  });

  const { t: i18nTCourses } = useTranslation('common', {
    keyPrefix: 'courses',
  });

  const {
    data: personnelQuery,
    isError: isPersonnelQueryError,
    isLoading: isPersonnelQueryLoading,
  } = usePersonnel({ personnelId, select: personnelQuerySelector });

  const {
    data: personnelTrainingQuery,
    isError: isPersonnelTrainingQueryError,
    isLoading: isPersonnelTrainingQueryLoading,
  } = usePersonnelTraining({
    personnelId,
    pollWhileAttachmentsProcess: true,
    select: personnelTrainingQuerySelector,
  });

  if (
    isPersonnelQueryLoading ||
    isPersonnelQueryError ||
    isPersonnelTrainingQueryLoading ||
    isPersonnelTrainingQueryError
  )
    return null;

  const allCoursesIndexedById = {
    ...personnelQuery.courses.indexedById,
    ...personnelTrainingQuery.courses.indexedById,
  };

  const course = allCoursesIndexedById[courseId];
  const booking = personnelQuery.bookings.indexedByCourseId[course.id];
  const booker =
    booking ?
      personnelQuery.bookers.indexedById[booking.relationships.booker.data?.id]
    : null;
  const registration =
    personnelQuery.registrations.indexedByCourseId[course.id];
  const latestTraining =
    (personnelTrainingQuery.training.groupedByCourseId[course.id] &&
      personnelTrainingQuery.training.groupedByCourseId[course.id][0]) ||
    null;
  const attachments =
    personnelTrainingQuery.attachments.groupedByTrainingId[
      latestTraining?.id
    ] || [];

  const currentTrainingStatus = getTrainingStatus({
    course,
    training: latestTraining,
  });

  return (
    <>
      <div className='tw-sticky tw-top-0 tw-z-[999] tw-flex tw-h-20 tw-w-full tw-items-center tw-bg-grey-900 tw-p-3'>
        <div className='tw-flex tw-h-full tw-w-13 tw-flex-none tw-items-center tw-border-0 tw-border-r-1 tw-border-solid tw-border-grey-500'>
          <LeftArrowIcon
            className='tw-ml-0.5 tw-cursor-pointer'
            data-element-name='navigate-home-icon'
            height={40}
            onClick={onViewHomeClick}
            width={40}
          />
        </div>
        <div className='tw-ml-4 tw-mr-4 tw-flex-auto tw-text-grey-025'>
          <h1 className='tw-m-0 tw-font-mark-pro tw-text-xl tw-font-bold tw-tracking-tight'>
            {i18nTCourses('courseDetails')}
          </h1>
        </div>
      </div>
      <div className='tw-p-3'>
        <div className='tw-rounded-lg tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-p-4'>
          <div className='tw-mb-3'>
            <h2 className='tw-m-0 tw-text-2xl tw-font-semibold tw-tracking-tighter tw-text-grey-900'>
              {course.attributes.name}
            </h2>
            {!isRequired && (
              <span className='tw-mt-1 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-500'>
                {i18nT('optional')}
              </span>
            )}
          </div>
          <CombinedStatus
            currentTrainingStatus={currentTrainingStatus}
            isRequired={isRequired}
            latestTraining={latestTraining}
          />
          {booking && (
            <div className='tw-mt-6'>
              <BookingDetails
                booker={booker}
                booking={booking}
                course={course}
                registration={registration}
              />
            </div>
          )}
        </div>
        <div className='tw-mt-3'>
          <Evidence
            attachments={attachments}
            coverImagesIndexedById={
              personnelTrainingQuery.coverImages.indexedById
            }
          />
        </div>
      </div>
    </>
  );
}

CourseDetailsScreen.propTypes = {
  personnelId: PropTypes.string.isRequired,
  courseId: PropTypes.string.isRequired,
  isRequired: PropTypes.bool,
  onViewHomeClick: PropTypes.func.isRequired,
};

const personnelQuerySelector = (response) => {
  const { included } = response;

  return {
    courses: formatResources({
      resources: filterIncluded({ included, type: 'course' }),
      options: { indexedById: { path: 'id' } },
    }),
    bookings: formatResources({
      resources: filterIncluded({ included, type: 'booking' }),
      options: { indexedByCourseId: { path: 'relationships.course.data.id' } },
    }),
    bookers: formatResources({
      resources: filterIncluded({ included, type: 'simpleUser' }),
      options: { indexedById: { path: 'id' } },
    }),
    registrations: formatResources({
      resources: filterIncluded({ included, type: 'registration' }),
      options: { indexedByCourseId: { path: 'relationships.course.data.id' } },
    }),
  };
};

const personnelTrainingQuerySelector = (response) => {
  const { data } = response;
  const { included } = response;

  return {
    training: formatResources({
      resources: data,
      options: { groupedByCourseId: { path: 'relationships.course.data.id' } },
    }),
    courses: formatResources({
      resources: filterIncluded({ included, type: 'course' }),
      options: { indexedById: { path: 'id' } },
    }),
    attachments: formatResources({
      resources: filterIncluded({ included, type: 'attachment' }),
      options: {
        groupedByTrainingId: { path: 'relationships.attachable.data.id' },
      },
    }),
    coverImages: formatResources({
      resources: filterIncluded({ included, type: 'coverImage' }),
      options: { indexedById: { path: 'id' } },
    }),
  };
};
